import React from 'react';
import { useAvContext } from '@context/AvContextProvider';
import { ItemWithLogoBase, ItemWithLogoBaseProps, LogoBase, LogoBaseProps, logoStyle, StatusWithLogo } from '@fabric-ui/components';

export { logoStyle, StatusWithLogo };

export enum IconVariant {
  destinationsMap = 'destinationsMap',
  sourcesMap = 'sourcesMap',
  sourcesMapByName = 'sourcesMapByName',
  color = 'color',
  authTypesMap = 'authTypesMap',
}

// Asset resolution — must live in client because import.meta.glob is Vite-specific
// and the .webp files live in client/src/assets/logos/
const logoImages = import.meta.glob('../assets/logos/*.webp', {
  eager: true,
  import: 'default',
}) as Record<string, string>;

const logoByTypeMap = Object.fromEntries(
  Object.entries(logoImages).map(([path, url]) => {
    const filename = path.split('/').at(-1) ?? 'GENERIC_UPLOAD.webp';
    return [filename.replace('.webp', ''), url];
  })
);

const iconUrlMap = Object.fromEntries(
  Object.entries(logoImages).map(([path, url]) => {
    const filename = path.split('/logos/').at(-1) ?? 'GENERIC_UPLOAD.webp';
    return [`logos/${filename}`, url];
  })
);

export function resolveLogoSrc(type?: string, iconUrl?: string): string {
  if (iconUrl && iconUrlMap[iconUrl]) return iconUrlMap[iconUrl];
  const resolvedType = type || 'GENERIC_UPLOAD';
  return logoByTypeMap[resolvedType] || logoByTypeMap.GENERIC_UPLOAD;
}

type LogoProps = Omit<LogoBaseProps, 'src' | 'alt' | 'onError'> & {
  type?: string;
  iconUrl?: string;
};

export function Logo({ type, iconUrl, ...rest }: LogoProps) {
  const src = resolveLogoSrc(type, iconUrl);
  const onError = ({ target }: React.SyntheticEvent<HTMLImageElement>) => {
    const img = target as HTMLImageElement;
    img.onerror = null;
    img.src = logoByTypeMap.GENERIC_UPLOAD;
  };
  return <LogoBase src={src} alt={type} onError={onError} {...rest} />;
}

type ItemWithLogoProps = Omit<ItemWithLogoBaseProps, 'displayName' | 'logoSrc' | 'logoAlt'> & {
  variant: IconVariant;
  type: string;
};

export function ItemWithLogo({ variant, type, ...rest }: ItemWithLogoProps) {
  const { typeNameMap } = useAvContext();
  const logoProps = rest.isAvatar
    ? {}
    : variant === IconVariant.destinationsMap
      ? typeNameMap.destinationsObj[variant][type]
      : variant === IconVariant.authTypesMap
        ? typeNameMap.authenticationSourcesObj[variant][type]
        : typeNameMap.sourcesObj[variant][type];

  const displayName = logoProps?.name || type || 'Unknown';
  const logoSrc = resolveLogoSrc(logoProps?.type ?? type, logoProps?.iconUrl);

  return <ItemWithLogoBase displayName={displayName} logoSrc={logoSrc} logoAlt={logoProps?.type ?? type} {...rest} />;
}
